import { createSlice, createEntityAdapter, PayloadAction, EntityAdapter } from '@reduxjs/toolkit'
import type { ProjectModel } from '@itemize/server/models'
import { api } from '../../api.ts'

const projectAdapter = createEntityAdapter({
  selectId: (project: ProjectModel) => project.id,
  sortComparer: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
})

export interface ProjectSliceState {
  entities: ReturnType<typeof projectAdapter.getInitialState>
  selectedProjectId: string | null
  activeProjectId: string | null
  filters: {
    search: string
    status: 'active' | 'completed' | 'archived' | 'all'
  }
}

const initialState: ProjectSliceState = {
  entities: projectAdapter.getInitialState(),
  selectedProjectId: null,
  activeProjectId: null,
  filters: {
    search: '',
    status: 'all',
  },
}

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    selectProject: (state, action: PayloadAction<string | null>) => {
      state.selectedProjectId = action.payload
    },
    setActiveProject: (state, action: PayloadAction<string | null>) => {
      state.activeProjectId = action.payload
    },
    setProjectSearchFilter: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
    },
    setProjectStatusFilter: (
      state,
      action: PayloadAction<'active' | 'completed' | 'archived' | 'all'>,
    ) => {
      state.filters.status = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        api.endpoints.getProjects.matchFulfilled,
        (state, action: PayloadAction<ProjectModel[]>) => {
          projectAdapter.setAll(state.entities, action.payload)
        },
      )
      .addMatcher(
        api.endpoints.getProject.matchFulfilled,
        (state, action: PayloadAction<ProjectModel>) => {
          projectAdapter.upsertOne(state.entities, action.payload)
        },
      )
  },
})

export const { selectProject, setActiveProject, setProjectSearchFilter, setProjectStatusFilter } =
  projectSlice.actions

export const projectSelectors = projectAdapter.getSelectors(
  (state: { project: ProjectSliceState }) => state.project.entities,
)

export const selectSelectedProjectId = (state: { project: ProjectSliceState }) =>
  state.project.selectedProjectId

export const selectActiveProjectId = (state: { project: ProjectSliceState }) =>
  state.project.activeProjectId

export const selectProjectFilters = (state: { project: ProjectSliceState }) => state.project.filters

export default projectSlice.reducer
